import type { GetServerSideProps } from 'next';
import { useRouter } from 'next/router';
import { useEffect } from 'react';

import ErrorPage from 'components/error-page';
import { Layout } from 'components/layout';
import TalkPage from 'components/pages/dubbing/slug';
import { TalkPageContextProvider } from 'components/pages/dubbing/slug/talk-page-context';

import { useNavUserInformationQuery } from 'api';
import acceptedTalks from 'components/pages/dubbing/slug/accepted-talks.json';
import { host } from 'f';
import { getTranscript } from 'lib/get-transcript';
import type { TalkPageActionProps } from 'lib/pages/talks/slug/[...slug].props';
import { getProps } from 'lib/pages/talks/slug/getProps';
import { useFeatureFlags } from 'lib/pages/talks/slug/store';

import authorizedUsers from '../../../authorized-users.json';

export default function Talk({
  action,
  videoData,
  transcriptData,

  commentsEnabled,
  commentsLoggedInOnly
}: TalkPageActionProps): React.ReactNode {
  const router = useRouter();
  const { data } = useNavUserInformationQuery();

  const { updateFlag } = useFeatureFlags();

  useEffect(() => {
    const isAuthorizedUser =
      Boolean(data?.viewer?.primaryEmailAddress) &&
      authorizedUsers.includes(data.viewer.primaryEmailAddress);

    updateFlag('newTalkPageExperience', isAuthorizedUser);
  }, [data, updateFlag]);

  switch (action?.type) {
    case 'client error':
    case 'redacted':
    case 'not found':
      return <ErrorPage statusCode={action.code} />;
    case 'redirect':
      // Replace the history entry don't make a new one. This only happens for
      // navigation within the SPA. (Bad link to a video id for example)
      router.replace(action.location);
      break;
    default:
      return (
        <Layout>
          <TalkPageContextProvider>
            <TalkPage
              videoData={videoData}
              transcript={transcriptData}
              commentsEnabled={commentsEnabled}
              commentsLoggedInOnly={commentsLoggedInOnly}
            />
          </TalkPageContextProvider>
        </Layout>
      );
  }
}

export const getServerSideProps: GetServerSideProps = async ctx => {
  const { res, query } = ctx;
  res.setHeader(
    'Cache-Control',
    'no-transform, public, s-maxage=180, stale-while-revalidate=60'
  );

  const path = res.req.url as string;
  const props: TalkPageActionProps = await getProps({ query, asPath: path });

  const slug = props?.videoData?.slug;

  const url = new URL(`/dubbing/${query.slug?.[0]}`, host);
  Object.entries(query)
    .filter(([key]) => key !== 'slug')
    .forEach(([key, value]) => {
      url.searchParams.append(key, value as string);
    });

  const language = props.videoData?.language || 'en';
  const transcript = await getTranscript(slug, language);

  if (props.action?.type === 'redirect') {
    res.writeHead(props.action.code, { Location: props.action.location });
    res.end();
    return { props: {} }; // You need to return something, even if the response has ended
  }

  const acceptedTalk = acceptedTalks.find(talk => talk.slug === slug);

  if (props.action?.code) {
    res.statusCode = props.action.code;
  }

  return {
    props: {
      action: acceptedTalk ? props.action : { type: 'not found', code: 404 },
      videoData: props.videoData || {},
      transcriptData: transcript,
      commentsEnabled: !!props?.videoData?.commentsEnabled,
      commentsLoggedInOnly: !!props?.videoData?.commentsLoggedInOnly
    }
  };
};
